import { ChangeDetectionStrategy, Component, effect, input, untracked } from '@angular/core';
import moment from 'moment';
import { Select2ScrollEvent, Select2SearchEvent } from 'ng-select2-component';
import { objectToCamel } from 'ts-case-convert';
import { APP } from 'src/app/config/app';
import { IBase, IFilterOption, IFilterReceiversValue } from 'src/app/models';
import {
  CommunicationUserType,
  InputType,
  MessageReceiver,
  UserRole,
  UserSortType,
  UserType,
} from 'src/app/models/enums';
import { RouteService, StorageService, TripsService, UsersService, UtilsService } from 'src/app/services';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { ServiceInfoService } from 'src/app/services/service-info/service-info.service';
import { FilterOptionList } from '../filterOptionList';

import { CommonModule } from '@angular/common';
import { Select2 } from 'ng-select2-component';
import { InputComponent } from '../../input/input.component';
@Component({
  selector: 'app-filter-receivers',
  templateUrl: './filter-receivers.component.html',
  imports: [CommonModule, Select2, InputComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class FilterReceiversComponent {
  readonly communicationUserType = input<CommunicationUserType>();
  readonly towns = input<IBase[]>([]);
  readonly selection = input<IFilterReceiversValue | null>(null);

  readonly MessageReceiver = MessageReceiver;
  readonly InputType = InputType;

  receiverTypes: IFilterOption[] = [];
  selectedType?: MessageReceiver;

  receiverList?: FilterOptionList;
  serviceList?: FilterOptionList;
  routeList?: FilterOptionList;

  private adminRole: UserRole;
  private setupKey?: string;

  constructor(
    public literalService: LiteralService,
    private storageService: StorageService,
    private usersService: UsersService,
    private utilsService: UtilsService,
    private serviceInfoService: ServiceInfoService,
    private routeService: RouteService,
    private tripsService: TripsService,
  ) {
    effect(() => {
      const key = `${this.communicationUserType()}|${JSON.stringify(this.selection())}`;
      if (key === this.setupKey) return;
      this.setupKey = key;
      untracked(() => this.setup());
    });
  }

  private async setup() {
    this.adminRole = await this.storageService.getRole();
    this.buildTypes();
    await this.restore();
  }

  private buildTypes() {
    this.receiverTypes = Object.values(MessageReceiver)
      .filter((receiver) => this.offers(receiver))
      .map((receiver) => ({
        id: receiver,
        value: receiver,
        label: this.literalService.get(`messages.receiver.${receiver.toLowerCase()}`, true),
      }));
  }

  private offers(receiver: MessageReceiver): boolean {
    const userType = this.communicationUserType();
    if (userType === CommunicationUserType.USERS && receiver === MessageReceiver.DRIVER) return false;
    if (
      userType === CommunicationUserType.DRIVERS &&
      (receiver === MessageReceiver.USER || receiver === MessageReceiver.TOWN || receiver === MessageReceiver.SERVICE)
    ) {
      return false;
    }
    const managesAreas = this.adminRole === UserRole.SUPERADMIN || this.adminRole === UserRole.ADMIN;
    if (!managesAreas && (receiver === MessageReceiver.TOWN || receiver === MessageReceiver.SERVICE)) return false;
    return true;
  }

  private async restore() {
    const selection = this.selection();
    if (!selection?.receiverType || !this.offers(selection.receiverType)) {
      this.clear();
      return;
    }
    await this.changeType(selection.receiverType);
    if (selection.receiverType === MessageReceiver.EXPEDITION) {
      if (!selection.expeditionService) return;
      await this.changeService(selection.expeditionService);
      if (!selection.expeditionRoute) return;
      await this.changeRoute(selection.expeditionRoute);
    }
    this.receiverList!.selectValues(selection.receivers);
  }

  value(): IFilterReceiversValue {
    return {
      receiverType: this.selectedType,
      expeditionService: this.single(this.serviceList),
      expeditionRoute: this.single(this.routeList),
      receivers: (this.receiverList?.selected ?? []).map((option) => option.value),
    };
  }

  clear() {
    this.selectedType = undefined;
    this.receiverList = undefined;
    this.serviceList = undefined;
    this.routeList = undefined;
  }

  async changeType(type: MessageReceiver) {
    this.selectedType = type;
    this.serviceList = undefined;
    this.routeList = undefined;
    this.receiverList = new FilterOptionList(this.loaderFor(type), this.mapperFor(type));
    if (type !== MessageReceiver.EXPEDITION) {
      await this.receiverList.reload();
      return;
    }
    this.receiverList.disabled = true;
    this.serviceList = new FilterOptionList(
      (page, search) => this.serviceInfoService.filterServiceInfo(page, FilterOptionList.pageSize(), undefined, search),
      this.mapperFor(MessageReceiver.SERVICE),
    );
    await this.serviceList.reload();
  }

  async changeService(service: IBase) {
    if (!this.serviceList) return;
    this.serviceList.selectValues([service]);
    this.receiverList!.clear();
    this.receiverList!.disabled = true;
    this.routeList = new FilterOptionList(
      (page, search) =>
        this.routeService.getRoutesSearch([service.id!], page, FilterOptionList.pageSize(), search) as any,
      (route: any) => ({ id: route.id, value: route, label: route.name }),
    );
    await this.routeList.reload();
  }

  async changeRoute(route: IBase) {
    if (!this.routeList) return;
    this.routeList.selectValues([route]);
    this.receiverList!.clear();
    this.receiverList!.disabled = false;
    await this.receiverList!.reload();
  }

  onTypeChange(event: any) {
    this.changeType(event.value as MessageReceiver);
  }

  onServiceChange(service: any) {
    if (service) this.changeService(service);
  }

  onRouteChange(route: any) {
    if (route) this.changeRoute(route);
  }

  onReceiversChange(selected: any[] | any) {
    this.receiverList?.selectValues(Array.isArray(selected) ? selected : [selected]);
  }

  onExpeditionChange(expedition: any) {
    this.receiverList?.selectValues([expedition]);
  }

  async onScroll(event: Select2ScrollEvent, list?: FilterOptionList) {
    if (event.way !== 'down') return;
    await list?.loadMore();
  }

  async onSearch(event: Select2SearchEvent, list?: FilterOptionList) {
    await list?.searchFor(event.search);
  }

  single(list?: FilterOptionList): any {
    return list?.selected[0]?.value;
  }

  searchable(): boolean {
    return this.selectedType !== MessageReceiver.TOWN;
  }

  placeholder(): string {
    return this.selectedType
      ? this.literalService.get(`messages.receiver.${this.selectedType.toLowerCase()}`, true)
      : '';
  }

  private loaderFor(type: MessageReceiver) {
    switch (type) {
      case MessageReceiver.USER:
      case MessageReceiver.DRIVER:
        return (page: number, search?: string) =>
          this.usersService.filterByUserType(
            type === MessageReceiver.DRIVER ? UserType.Driver : UserType.Regular,
            UserSortType.ALPHABETICALLY,
            page,
            FilterOptionList.pageSize(),
            [],
            [],
            [],
            APP.MESSAGE_RECEIVER_USER_STATUS,
            search,
          ) as any;
      case MessageReceiver.TOWN:
        return () => Promise.resolve({ content: this.towns(), last: true });
      case MessageReceiver.SERVICE:
        return (page: number, search?: string) =>
          this.serviceInfoService.filterServiceInfo(page, FilterOptionList.pageSize(), undefined, search);
      case MessageReceiver.EXPEDITION:
        return (page: number, search?: string) =>
          this.tripsService.getTripsFiltered(
            FilterOptionList.pageSize(),
            page,
            [],
            [],
            [],
            [],
            APP.MESSAGE_RECEIVER_EXPEDITION_STATUS,
            search,
            undefined,
            [{ id: this.single(this.routeList)?.id } as IBase],
            true,
          ) as any;
    }
  }

  private mapperFor(type: MessageReceiver): (item: any) => IFilterOption {
    switch (type) {
      case MessageReceiver.USER:
      case MessageReceiver.DRIVER:
        return (user: any) => ({
          id: user.id,
          value: this.utilsService.sanitizeObject(user),
          label: this.utilsService.getUserLabel(user),
        });
      case MessageReceiver.TOWN:
        return (town: any) => ({ id: town.id, value: town, label: town.name });
      case MessageReceiver.SERVICE:
        return (service: any) => ({
          id: service.id,
          value: service,
          label: service.name ? `${service.tag} - ${service.name}` : service.tag,
        });
      case MessageReceiver.EXPEDITION:
        return (item: any) => {
          const trip: any = objectToCamel(item);
          return { id: trip.id, value: trip, label: moment(trip.dateTime).format('DD/MM HH:mm') };
        };
    }
  }
}
